"use client";

import React, {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  useSyncExternalStore
} from "react";
import { ChannelSearchClient } from "../../molecules/channel-search/channel-search.client";
import type {
  ChannelSearchItem,
  ChannelSearchSelectionChange
} from "../../molecules/channel-search/channel-search.shared";
import {
  LINK_POSITION_CHANNEL_LINEUP_PANEL,
  fireLinkClick,
  fireSearch
} from "../../services/analytics";
import {
  CHANNEL_LINEUP_SEARCH_CATEGORY,
  RESET_SEARCH_LINK_NAME,
  SEARCH_FIELD_LINK_NAME,
  chipRemoveLinkName,
  pageHref,
  selectAllLinkName,
  selectAllSearchTerm,
  suggestionLinkName,
  suggestionSearchTerm
} from "./lineup-analytics.shared";
import {
  EMPTY_LINEUP_SEARCH_SELECTION,
  type LineupSearchItemOptions,
  type LineupSearchSelection,
  type LineupSearchSource,
  buildLineupSearchItems,
  isSameLineupSearchMarket,
  lineupSearchSelectionOf,
  lineupSelectionLimitOf,
  reconcileLineupSearchSelection
} from "./lineup-search.shared";
import { useLineupMarketRows } from "./lineup-market.client";
import { withMarketSource } from "./lineup-market.shared";
import { withSafeRender } from "../../atoms/safe-render";

/**
 * Channel Lineup search — the wiring between `molecules/channel-search` and the lineup (SR1-SR12,
 * AX11, ZP12, and SR11's seam). The `"use client"` argument is the seam's, not a second copy of the
 * molecule's.
 */

interface LineupSearchContextValue {
  /** The market's items plus ZP12's carried-over selections. */
  items: readonly ChannelSearchItem[];
  selectedIds: readonly string[];
  onSelectionChange: (
    ids: string[],
    change: ChannelSearchSelectionChange
  ) => void;
  onReset: () => void;
  /** SR11 — what WAT-12336 and WAT-12337 read. */
  selection: LineupSearchSelection;
}

interface LineupSearchStore {
  get: () => LineupSearchContextValue;
  subscribe: (listener: () => void) => () => void;
  publish: (next: LineupSearchContextValue) => void;
}

/* A store, not A context value that changes — the same reason as `createLineupViewStore`: a changed
   provider value makes React walk every fiber of the lineup on each chip. */
function createLineupSearchStore(initial: LineupSearchContextValue): LineupSearchStore {
  const listeners = new Set<() => void>();
  let snapshot = initial;

  return {
    get: () => snapshot,
    subscribe: listener => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    publish: next => {
      if (next !== snapshot) {
        snapshot = next;
        listeners.forEach(listener => listener());
      }
    }
  };
}

const useIsomorphicLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;

const LineupSearchContext = createContext<LineupSearchStore | null>(null);

const NO_STORE_SUBSCRIBE = () => () => {};

interface SelectionState {
  selectedIds: string[];
  /** ZP12 clause 3 — selections this market does not carry, kept so the chip
   *  survives. Held as STATE because once `items` flips the old item object is
   *  gone and cannot be recomputed. */
  carried: ChannelSearchItem[];
  matchedIds: Record<string, string[]>;
}

export interface LineupSearchProviderProps {
  /**
   * Every row the reader can find, from `buildLineupSearchItems`. A new set of rows is what A ZIP
   * change looks like from here — the id set, not the array identity (`isSameLineupSearchMarket`).
   */
  items: readonly ChannelSearchItem[];
  /** Plan §1.2 — the URL stays authoritative for the first paint. */
  initialSelectedIds?: readonly string[];
  children: React.ReactNode;
}

function LineupSearchProviderImpl({
  items,
  initialSelectedIds,
  children
}: LineupSearchProviderProps) {
  const [state, setState] = useState<SelectionState>(() => {
    const offered = new Set(items.map(item => item.id));
    return {
      /* An ID this market does not carry is dropped at mount, not held. */
      selectedIds: (initialSelectedIds ?? []).filter(id => offered.has(id)),
      carried: [],
      matchedIds: {}
    };
  });
  const [seenItems, setSeenItems] = useState(items);

  /* ZP12, adjusted during render rather than in an effect: an effect gives one committed render
     holding ids that belong to the previous market, which the reader sees as the chips flickering. */
  if (seenItems !== items) {
    setSeenItems(items);
    /* ZP12 — and only on a real market change. */
    if (!isSameLineupSearchMarket(seenItems, items)) {
      setState(current => reconcileLineupSearchSelection(
        /* The previous market and what was already being carried — without the carried half, a
           selection carried across ZIP 1 → 2 loses its label and is dropped on ZIP 2 → 3. */
        [...seenItems, ...current.carried],
        items,
        current.selectedIds
      ));
    }
  }

  const value = useMemo<LineupSearchContextValue>(() => {
    /**
     * AN5 — "selecting a search suggestion sends a search event (category `channelLineup`) with the
     * suggestion's name in lower case", and the select-all row sends one event spelt from the
     * prefix and the typed text.
     */
    const reportSelection = (change: ChannelSearchSelectionChange) => {
      if (change.reason === "select") {
        fireSearch({
          searchCategory: CHANNEL_LINEUP_SEARCH_CATEGORY,
          searchTerm: suggestionSearchTerm(change.item.label)
        });
        /* AN5's second half — the same choice is ALSO a link click, under a
           different wording from the search term above. */
        fireLinkClick({
          linkDestination: pageHref(),
          linkName: suggestionLinkName(change.item.label, change.item.secondary),
          linkPosition: LINK_POSITION_CHANNEL_LINEUP_PANEL
        });
        return;
      }

      if (change.reason === "remove-chip") {
        /* AN5 — "removing a chip sends a link click named 'remove {channel name} channel
           {number}'", and no search event: the selection shrank. */
        fireLinkClick({
          linkDestination: pageHref(),
          linkName: chipRemoveLinkName(change.item.label, change.item.secondary),
          linkPosition: LINK_POSITION_CHANNEL_LINEUP_PANEL
        });
        return;
      }

      if (change.reason === "select-all") {
        fireSearch({
          searchCategory: CHANNEL_LINEUP_SEARCH_CATEGORY,
          /* The two halves joined with no space, where the link name below joins them with a
             hyphen. One interaction, two spellings, both already on the layer — see
             `selectAllSearchTerm`. */
          searchTerm: selectAllSearchTerm(change.prefix, change.typedText)
        });
        fireLinkClick({
          linkDestination: pageHref(),
          linkName: selectAllLinkName(change.prefix, change.typedText),
          linkPosition: LINK_POSITION_CHANNEL_LINEUP_PANEL
        });
      }

      /* `deselect` and `reset` are the two that report NOTHING here. Both
         shrink the selection and AN5 asks for no search on either; `reset` is
         AN8's and is named on `onReset` below, where the ✕ reaches it too. */
    };

    const onSelectionChange = (
      ids: string[],
      change: ChannelSearchSelectionChange
    ) => {
      reportSelection(change);
      setState(current => {
        const kept = new Set(ids);
        const matchedIds: Record<string, string[]> = {};
        for (const id of ids) {
          const matches = current.matchedIds[id];
          if (matches) {
            matchedIds[id] = matches;
          }
        }
        return {
          selectedIds: ids,
          /* A carried chip that is removed takes its row out of the suggestion
             list with it — nothing else in this market offers that channel. */
          carried: current.carried.filter(item => kept.has(item.id)),
          matchedIds
        };
      });
    };

    /* A carried chip's row is still findable in A market that does not carry it, and that is the
       cost of ZP12 clause 3, not a defect: `molecules/channel-search` resolves a chip out of
       `items` by id (`selection.ts:73-86`), so the row has to be present for the chip to draw at
       all. */
    const offered =
      state.carried.length === 0 ? items : [...items, ...state.carried];

    return {
      items: offered,
      selectedIds: state.selectedIds,
      onSelectionChange,
      onReset: () => {
        /* AN8 — "Reset Search, named 'reset search', in the channel lineup panel". */
        fireLinkClick({
          linkDestination: pageHref(),
          linkName: RESET_SEARCH_LINK_NAME,
          linkPosition: LINK_POSITION_CHANNEL_LINEUP_PANEL
        });
        setState({
          selectedIds: [],
          carried: [],
          matchedIds: {}
        });
      },
      selection: lineupSearchSelectionOf(
        state.selectedIds,
        state.matchedIds,
        offered
      )
    };
  }, [items, state]);

  /* Seeded with the first paint's value; later values are published after
     commit, so consumers read a settled snapshot and the provider's own
     re-render never touches the fibers below it. */
  const [store] = useState(() => createLineupSearchStore(value));
  useIsomorphicLayoutEffect(() => {
    store.publish(value);
  }, [store, value]);

  return (
    <LineupSearchContext.Provider value={store}>{children}</LineupSearchContext.Provider>
  );
}
export const LineupSearchProvider = withSafeRender(LineupSearchProviderImpl, "LineupSearchProvider");

export interface LineupSearchSourceProviderProps
  extends Omit<LineupSearchProviderProps, "items"> {
  /** The resolve's `searchSource` — the same arrays the views are handed. */
  source: LineupSearchSource;
  /** The authored wording on the suggestion rows: the subscriptions heading and the badges. */
  itemOptions?: LineupSearchItemOptions;
}

/** `LineupSearchProvider` fed from the resolve's arrays rather than a built list. */
function LineupSearchSourceProviderImpl({ source, itemOptions, ...props }: LineupSearchSourceProviderProps) {
  const marketRows = useLineupMarketRows();
  const items = useMemo(
    () => buildLineupSearchItems(withMarketSource(source, marketRows), itemOptions),
    [source, marketRows, itemOptions]
  );

  return <LineupSearchProvider {...props} items={items} />;
}
export const LineupSearchSourceProvider = withSafeRender(LineupSearchSourceProviderImpl, "LineupSearchSourceProvider");

/**
 * SR11 — the selection, for WAT-12336 (the Signature grid under search) and WAT-12337 (Genre Packs
 * under search).
 */
export function useLineupSearchSelection(): LineupSearchSelection {
  const store = useContext(LineupSearchContext);
  const getSelection = store ? () => store.get().selection : () => EMPTY_LINEUP_SEARCH_SELECTION;
  const selection = useSyncExternalStore(
    store ? store.subscribe : NO_STORE_SUBSCRIBE,
    getSelection,
    getSelection
  );

  if (process.env.NODE_ENV !== "production" && !store) {
    console.warn(
      "[LineupSearch] a view read the search selection from outside " +
        "<LineupSearchProvider>, so it will never filter (SR11). The provider " +
        "has to wrap the WHOLE lineup — wrapping only `searchSlot` gives a " +
        "working field whose selections reach nothing."
    );
  }

  return selection;
}

/**
 * SR8 plus the field, as one unit — what goes in the root's `searchSlot`. Naming (plan §1.6):
 * snake_case for author-typed CMS scalars, which is every prop here — the same split
 * `genre-pack-list.tsx` publishes.
 */
export interface LineupSearchProps {
  /** Unique on the page; every id the field mints hangs off it. */
  id?: string;
  /** SR1 — the content-managed prompt, and the field's accessible name. */
  field_label?: string;
  /** Names the search landmark. Distinct from `field_label`. */
  aria_label?: string;
  /** The accessible name of the ✕. */
  clear_aria_label?: string;
  /** SR2 — shown at exactly one character typed. */
  typing_hint_content?: string;
  /** SR5 — the select-all row reads "{prefix} {typed text}". */
  select_all_prefix_label?: string;
  /** SR5 — the heading channel rows sit under. */
  group_heading_content?: string;
  /** SR9 — what the list says when nothing matched. */
  empty_message_content?: string;
  /** SR8 — written, the Reset control renders and the ✕ clears only the text.
   *  Absent, there is no Reset control and the ✕ clears both. */
  reset_label?: string;
  /** AX11 — the verb in a chip's remove button. */
  chip_remove_aria_label?: string;
  /** SR7 — the overflow toggle's two labels. */
  show_more_label?: string;
  show_less_label?: string;
  /**
   * SR10 — the content team's selection limit. None is set today. Blank, zero, or anything that is
   * not a whole number of 1 or more is no limit, not a limit of none — see
   * `lineupSelectionLimitOf`.
   */
  max_selections?: number;
  className?: string;
}

const DEFAULT_ID = "channel-lineup-search";

/**
 * AN5's last clause — "clicking into the search field sends a link click named `Search Channels`".
 * A push, and the field carries no `data-linkposition`.
 */
function reportFieldActivate(): void {
  fireLinkClick({
    linkDestination: pageHref(),
    linkName: SEARCH_FIELD_LINK_NAME,
    linkPosition: LINK_POSITION_CHANNEL_LINEUP_PANEL
  });
}

function LineupSearchImpl({
  id = DEFAULT_ID,
  field_label: fieldLabel,
  aria_label: ariaLabel,
  clear_aria_label: clearLabel,
  typing_hint_content: typingHint,
  select_all_prefix_label: selectAllPrefix,
  group_heading_content: groupHeading,
  empty_message_content: emptyMessage,
  reset_label: resetLabel,
  chip_remove_aria_label: chipRemoveLabel,
  show_more_label: showMoreLabel,
  show_less_label: showLessLabel,
  max_selections: maxSelections,
  className
}: LineupSearchProps) {
  /* SR10 — an authored scalar reaches the molecule's limit gate, so it is coerced here rather than
     trusted. A blank or zero field would otherwise disable the search on first paint; the whole
     argument is in `lineupSelectionLimitOf`. */
  const selectionLimit = lineupSelectionLimitOf(maxSelections);

  const store = useContext(LineupSearchContext);
  if (!store) {
    /* A developer error, not a data one: the field cannot hold a selection the
       views also read, so rendering a dead search would be worse than saying
       so. The provider wraps the whole lineup — see this file's header. */
    throw new Error(
      "[LineupSearch] must be rendered inside <LineupSearchProvider>."
    );
  }
  const context = useSyncExternalStore(store.subscribe, store.get, store.get);

  return (
    <ChannelSearchClient
      ariaLabel={ariaLabel}
      chipRemoveLabel={chipRemoveLabel}
      className={className}
      clearLabel={clearLabel}
      emptyMessage={emptyMessage}
      groupHeading={groupHeading}
      id={id}
      items={context.items}
      label={fieldLabel}
      maxSelections={selectionLimit}
      onFieldActivate={reportFieldActivate}
      onReset={context.onReset}
      onSelectionChange={context.onSelectionChange}
      resetLabel={resetLabel}
      selectAllPrefix={selectAllPrefix}
      selectedIds={context.selectedIds}
      showLessLabel={showLessLabel}
      showMoreLabel={showMoreLabel}
      typingHint={typingHint}
    />
  );
}
export const LineupSearch = withSafeRender(LineupSearchImpl, "LineupSearch");

export default LineupSearch;
